import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import { GooglePlacesAddressInput } from './common/GooglePlacesAddressInput';
import { MapPin, RefreshCw, Search, ChevronDown, Plus, Edit2, Trash2, XCircle, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { showDeleteConfirmation } from '../lib/sweetAlert';

interface TempleEvent {
  _id: string;
  name: string;
  type: string;
  description?: string;
  eventRecords?: {
    date: string;
    time: string;
    activity: string;
    description?: string;
  }[];
  locationType?: string;
  offSiteAddress?: string;
  offSiteCoordinates?: { lat: number; lng: number };
  templeId?: { _id: string; name: string };
  status?: string;
  entryPrice?: number;
  currency?: string;
  isPublic?: boolean;
  enableVolunteer?: boolean;
  enableFamilyMemberAttribution?: boolean;
  sponsorshipItems?: { name: string; cost: number; description?: string }[];
  bannerImage?: string;
}

const EMPTY_FORM = {
  name: '',
  type: 'festival' as string,
  description: '',
  eventRecords: [{ date: '', time: '', activity: '', description: '' }],
  locationType: 'In-Temple' as 'In-Temple' | 'Off-site',
  offSiteAddress: '',
  offSiteCoordinates: undefined as { lat: number; lng: number } | undefined,
  templeId: '',
  entryPrice: 0,
  currency: 'USD',
  isPublic: true,
  enableVolunteer: false,
  enableFamilyMemberAttribution: false,
  sponsorshipItems: [] as { name: string; cost: number; description: string }[],
};

const EventsPage: React.FC = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState<TempleEvent[]>([]);
  const [temples, setTemples] = useState<{ _id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [templeFilter, setTempleFilter] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState<TempleEvent | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const fetchTemples = useCallback(async () => {
    if (!isSuperAdmin) return;
    const res = await adminApiClient.getTemples();
    if (res.success && res.data?.temples) setTemples(res.data.temples);
  }, [isSuperAdmin]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApiClient.getTempleEvents({
        limit: 200,
        ...(search && { search }),
        ...(isSuperAdmin && templeFilter && templeFilter !== 'all' && { templeId: templeFilter }),
      });
      if (res.success && (res as any).data?.events) {
        setEvents((res as any).data.events);
      }
    } catch {
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  }, [search, isSuperAdmin, templeFilter]);

  useEffect(() => { fetchTemples(); }, [fetchTemples]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setEditingEvent(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (event: TempleEvent) => {
    setEditingEvent(event);
    setFormData({
      name: event.name || '',
      type: event.type || 'festival',
      description: event.description || '',
      eventRecords: event.eventRecords?.length ? event.eventRecords.map(r => ({
        date: r.date ? format(new Date(r.date), 'yyyy-MM-dd') : '',
        time: r.time || '',
        activity: r.activity || '',
        description: r.description || ''
      })) : [{ date: '', time: '', activity: '', description: '' }],
      locationType: (event.locationType as 'In-Temple' | 'Off-site') || 'In-Temple',
      offSiteAddress: event.offSiteAddress || '',
      offSiteCoordinates: event.offSiteCoordinates,
      templeId: typeof event.templeId === 'object' ? event.templeId?._id : (event.templeId as any) || '',
      entryPrice: event.entryPrice ?? 0,
      currency: event.currency || 'USD',
      isPublic: event.isPublic ?? true,
      enableVolunteer: event.enableVolunteer ?? false,
      enableFamilyMemberAttribution: event.enableFamilyMemberAttribution ?? false,
      sponsorshipItems: (event.sponsorshipItems || []).map(s => ({
        name: s.name,
        cost: s.cost,
        description: s.description || '',
      })),
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await showDeleteConfirmation('Delete Event?', 'This action cannot be undone.');
    if (!confirmed) return;
    try {
      const res = await adminApiClient.deleteTempleEvent(id);
      if (res.success) {
        toast.success('Event deleted');
        fetchData();
      } else {
        toast.error(res.message || 'Failed to delete');
      }
    } catch {
      toast.error('Error deleting event');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSuperAdmin && !editingEvent && !formData.templeId) {
      toast.error('Please select a temple');
      return;
    }
    if (formData.locationType === 'Off-site' && !formData.offSiteAddress.trim()) {
      toast.error('Please enter an off-site address');
      return;
    }

    const toastId = toast.loading(editingEvent ? 'Updating event…' : 'Creating event…');
    setSubmitting(true);
    try {
      const payload: any = {
        name: formData.name,
        type: formData.type,
        description: formData.description,
        eventRecords: formData.eventRecords.filter(r => r.date && r.time && r.activity),
        locationType: formData.locationType,
        entryPrice: Number(formData.entryPrice),
        currency: formData.currency,
        isPublic: formData.isPublic,
        enableVolunteer: formData.enableVolunteer,
        enableFamilyMemberAttribution: formData.enableFamilyMemberAttribution,
        sponsorshipItems: formData.sponsorshipItems.filter(s => s.name.trim()),
      };

      if (formData.locationType === 'Off-site') {
        payload.offSiteAddress = formData.offSiteAddress;
        if (formData.offSiteCoordinates) {
          payload.offSiteCoordinates = formData.offSiteCoordinates;
        }
      } else {
        payload.offSiteAddress = '';
        payload.offSiteCoordinates = undefined;
      }

      if (isSuperAdmin && !editingEvent && formData.templeId) {
        payload.templeId = formData.templeId;
      }

      let res;
      if (editingEvent) {
        res = await adminApiClient.updateTempleEvent(editingEvent._id, payload);
      } else {
        res = await adminApiClient.createTempleEvent(payload);
      }

      if (res.success) {
        toast.success(editingEvent ? 'Event updated' : 'Event created', { id: toastId });
        setShowForm(false);
        resetForm();
        fetchData();
      } else {
        toast.error(res.message || 'Operation failed', { id: toastId });
      }
    } catch (err: any) {
      toast.error(err.message || 'Error saving event', { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  // Sponsorship helpers
  const addSponsorshipItem = () => {
    setFormData(prev => ({
      ...prev,
      sponsorshipItems: [...prev.sponsorshipItems, { name: '', cost: 0, description: '' }],
    }));
  };
  const removeSponsorshipItem = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      sponsorshipItems: prev.sponsorshipItems.filter((_, i) => i !== idx),
    }));
  };
  const updateSponsorshipItem = (idx: number, field: string, value: any) => {
    setFormData(prev => {
      const items = [...prev.sponsorshipItems];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, sponsorshipItems: items };
    });
  };

  // Event Records helpers
  const addEventRecord = () => {
    setFormData(prev => ({
      ...prev,
      eventRecords: [...prev.eventRecords, { date: '', time: '', activity: '', description: '' }],
    }));
  };
  const removeEventRecord = (idx: number) => {
    setFormData(prev => ({
      ...prev,
      eventRecords: prev.eventRecords.filter((_, i) => i !== idx),
    }));
  };
  const updateEventRecord = (idx: number, field: string, value: string) => {
    setFormData(prev => {
      const records = [...prev.eventRecords];
      records[idx] = { ...records[idx], [field]: value };
      return { ...prev, eventRecords: records };
    });
  };

  const columns: ColumnDef<TempleEvent>[] = [
    {
      accessorKey: 'name',
      header: 'Event Name',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">{row.original.name}</div>
          <div className="text-xs text-gray-500 capitalize">{row.original.type}</div>
        </div>
      ),
    },
    {
      accessorKey: 'templeId',
      header: 'Temple',
      cell: ({ row }) => {
        const temple = row.original.templeId;
        return temple && typeof temple === 'object' ? (temple as any).name : '-';
      },
    },
    {
      id: 'date',
      header: 'Date',
      cell: ({ row }) => {
        const records = row.original.eventRecords;
        if (!records || records.length === 0) return '-';
        const d = records[0].date;
        return d ? format(new Date(d), 'MMM d, yyyy') + (records.length > 1 ? ` (+${records.length - 1})` : '') : '-';
      },
    },
    {
      id: 'time',
      header: 'Time',
      cell: ({ row }) => row.original.eventRecords?.[0]?.time || '-',
    },
    {
      accessorKey: 'locationType',
      header: 'Location',
      cell: ({ row }) => (
        <div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
            row.original.locationType === 'Off-site'
              ? 'bg-amber-50 text-amber-700'
              : 'bg-green-50 text-green-700'
          }`}>
            <MapPin size={11} />
            {row.original.locationType || 'In-Temple'}
          </span>
          {row.original.locationType === 'Off-site' && row.original.offSiteAddress && (
            <div className="text-xs text-gray-500 mt-0.5 max-w-[180px] truncate">{row.original.offSiteAddress}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'isPublic',
      header: 'Status',
      cell: ({ row }) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
          row.original.isPublic ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'
        }`}>
          {row.original.isPublic ? 'Public' : 'Private'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => openEdit(row.original)}
            className="p-1.5 text-gray-500 hover:text-[var(--color-secondary)] hover:bg-[color-mix(in_srgb,var(--color-secondary)_10%,white)] rounded-lg transition-colors"
          >
            <Edit2 size={15} />
          </button>
          <button
            onClick={() => handleDelete(row.original._id)}
            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  const filteredEvents = events.filter(
    (e) => !search || e.name.toLowerCase().includes(search.toLowerCase()) || (e.description || '').toLowerCase().includes(search.toLowerCase())
  );

  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/20 focus:border-[var(--color-primary)] text-sm';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-sm text-gray-500">Manage temple events</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg hover:opacity-90 transition shadow-sm text-sm font-medium"
          >
            <Plus size={17} /> Add Event
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search events…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
          />
        </div>
        {isSuperAdmin && temples.length > 0 && (
          <div className="relative">
            <select
              value={templeFilter}
              onChange={(e) => setTempleFilter(e.target.value)}
              className="pl-4 pr-10 py-2 border border-gray-200 rounded-lg text-sm appearance-none bg-white min-w-[180px]"
            >
              <option value="all">All temples</option>
              {temples.map((t) => (
                <option key={t._id} value={t._id}>{t.name}</option>
              ))}
            </select>
            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredEvents}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>

      {/* Create / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8 animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">
                {editingEvent ? 'Edit Event' : 'New Event'}
              </h2>
              <button
                onClick={() => { setShowForm(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-full transition-colors"
              >
                <XCircle size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Two column grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Event Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    className={inputCls}
                    placeholder="e.g. Ganesh Chaturthi Celebration"
                  />
                </div>

                {/* Temple selector — super admin only */}
                {isSuperAdmin && !editingEvent && (
                  <div className="col-span-2">
                    <label className={labelCls}>Temple <span className="text-red-500">*</span></label>
                    <select
                      required
                      value={formData.templeId}
                      onChange={e => setFormData(p => ({ ...p, templeId: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="">Select Temple</option>
                      {temples.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                    </select>
                  </div>
                )}

                <div>
                  <label className={labelCls}>Event Type <span className="text-red-500">*</span></label>
                  <select
                    required
                    value={formData.type}
                    onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}
                    className={inputCls}
                  >
                    <option value="festival">Festival</option>
                    <option value="pooja">Pooja</option>
                    <option value="community">Community</option>
                    <option value="volunteer">Volunteer</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Currency</label>
                  <select
                    value={formData.currency}
                    onChange={e => setFormData(p => ({ ...p, currency: e.target.value }))}
                    className={inputCls}
                  >
                    {['USD', 'CAD', 'INR', 'EUR', 'GBP'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>

                <div className="col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelCls + ' mb-0'}>Event Records (Schedule) <span className="text-red-500">*</span></label>
                    <button
                      type="button"
                      onClick={addEventRecord}
                      className="text-xs text-[var(--color-primary)] hover:opacity-80 font-medium"
                    >
                      + Add Row
                    </button>
                  </div>
                  {formData.eventRecords.map((record, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 border border-gray-200 rounded-lg mb-2 relative">
                      {formData.eventRecords.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEventRecord(idx)}
                          className="absolute -top-2 -right-2 bg-red-100 text-red-500 rounded-full p-1 hover:bg-red-200"
                        >
                          <XCircle size={14} />
                        </button>
                      )}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
                          <input
                            type="date"
                            required
                            value={record.date}
                            onChange={e => updateEventRecord(idx, 'date', e.target.value)}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Time</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. 8 AM - 9 AM"
                            value={record.time}
                            onChange={e => updateEventRecord(idx, 'time', e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Activity</label>
                          <input
                            type="text"
                            required
                            placeholder="e.g. Aarti"
                            value={record.activity}
                            onChange={e => updateEventRecord(idx, 'activity', e.target.value)}
                            className={inputCls}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
                          <input
                            type="text"
                            placeholder="Optional"
                            value={record.description}
                            onChange={e => updateEventRecord(idx, 'description', e.target.value)}
                            className={inputCls}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Location Type */}
                <div>
                  <label className={labelCls}>Location Type <span className="text-red-500">*</span></label>
                  <select
                    value={formData.locationType}
                    onChange={e => setFormData(p => ({
                      ...p,
                      locationType: e.target.value as 'In-Temple' | 'Off-site',
                      offSiteAddress: '',
                      offSiteCoordinates: undefined,
                    }))}
                    className={inputCls}
                  >
                    <option value="In-Temple">In-Temple</option>
                    <option value="Off-site">Off-Site</option>
                  </select>
                </div>

                <div>
                  <label className={labelCls}>Entry Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    onWheel={e => (e.target as HTMLElement).blur()}
                    value={formData.entryPrice}
                    onChange={e => setFormData(p => ({ ...p, entryPrice: parseFloat(e.target.value) || 0 }))}
                    className={inputCls}
                    placeholder="0 = Free"
                  />
                </div>
              </div>

              {/* Off-site Address — conditionally shown */}
              {formData.locationType === 'Off-site' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                  <div className="flex items-center gap-2 text-amber-700">
                    <MapPin size={16} />
                    <span className="font-medium text-sm">Off-Site Location</span>
                  </div>
                  <div>
                    <label className={labelCls}>
                      Address <span className="text-red-500">*</span>
                      <span className="ml-1 text-gray-400 font-normal">(Start typing to search)</span>
                    </label>
                    <GooglePlacesAddressInput
                      value={formData.offSiteAddress}
                      onChange={v => setFormData(p => ({ ...p, offSiteAddress: v }))}
                      onAddressSelect={addr => {
                        setFormData(p => ({
                          ...p,
                          offSiteAddress: addr.fullAddress || p.offSiteAddress,
                          offSiteCoordinates:
                            addr.latitude && addr.longitude
                              ? { lat: addr.latitude, lng: addr.longitude }
                              : p.offSiteCoordinates,
                        }));
                      }}
                      placeholder="Search an address…"
                      className={inputCls}
                      required
                    />
                  </div>

                  {/* Mini preview map if coordinates exist */}
                  {formData.offSiteCoordinates && (
                    <div className="rounded-lg overflow-hidden border border-amber-200">
                      <iframe
                        title="Location Preview"
                        width="100%"
                        height="180"
                        loading="lazy"
                        style={{ border: 0 }}
                        src={`https://www.google.com/maps?q=${formData.offSiteCoordinates.lat},${formData.offSiteCoordinates.lng}&hl=en&z=15&output=embed`}
                      />
                    </div>
                  )}

                  {formData.offSiteCoordinates && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle size={12} />
                      Coordinates captured: {formData.offSiteCoordinates.lat.toFixed(5)}, {formData.offSiteCoordinates.lng.toFixed(5)}
                    </p>
                  )}
                </div>
              )}

              {/* Description */}
              <div>
                <label className={labelCls}>Description <span className="text-red-500">*</span></label>
                <textarea
                  required
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  className={inputCls}
                  placeholder="Describe the event…"
                />
              </div>

              {/* Options row */}
              <div className="grid grid-cols-3 gap-4">
                {[
                  { key: 'isPublic', label: 'Public Event' },
                  { key: 'enableVolunteer', label: 'Enable Volunteer' },
                  { key: 'enableFamilyMemberAttribution', label: 'Family Attribution' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={(formData as any)[opt.key]}
                      onChange={e => setFormData(p => ({ ...p, [opt.key]: e.target.checked }))}
                      className="w-4 h-4 text-[var(--color-primary)] rounded"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>

              {/* Sponsorship Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls + ' mb-0'}>Sponsorship Items</label>
                  <button
                    type="button"
                    onClick={addSponsorshipItem}
                    className="text-xs text-[var(--color-primary)] hover:opacity-80 font-medium"
                  >
                    + Add Item
                  </button>
                </div>
                {formData.sponsorshipItems.length === 0 && (
                  <p className="text-xs text-gray-400">No sponsorship items. Click "+ Add Item" to add one.</p>
                )}
                <div className="space-y-2">
                  {formData.sponsorshipItems.map((item, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <input
                        type="text"
                        value={item.name}
                        onChange={e => updateSponsorshipItem(idx, 'name', e.target.value)}
                        placeholder="Item name"
                        className={inputCls + ' col-span-5'}
                      />
                      <input
                        type="number"
                        min="0"
                        onWheel={e => (e.target as HTMLElement).blur()}
                        value={item.cost}
                        onChange={e => updateSponsorshipItem(idx, 'cost', parseFloat(e.target.value) || 0)}
                        placeholder="Cost"
                        className={inputCls + ' col-span-3'}
                      />
                      <input
                        type="text"
                        value={item.description}
                        onChange={e => updateSponsorshipItem(idx, 'description', e.target.value)}
                        placeholder="Description (optional)"
                        className={inputCls + ' col-span-3'}
                      />
                      <button
                        type="button"
                        onClick={() => removeSponsorshipItem(idx)}
                        className="col-span-1 p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => { setShowForm(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-5 py-2 text-white bg-[var(--color-primary)] hover:opacity-90 rounded-lg text-sm font-medium transition-opacity disabled:opacity-60"
                >
                  {submitting ? 'Saving…' : editingEvent ? 'Save Changes' : 'Create Event'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
